import React from 'react';
import { State } from '@economic/taco';
import { Trans } from '@lingui/macro';
import { AccountTypeNumber } from '~/types';
import {
    CSharpDateTime,
    SmartPayPaymentExtendedDetailsModel,
    SmartPayPaymentExtendedModel,
    SmartPayPaymentInvoiceModel,
    Supplier,
} from '../../../../../types/internal-api';

export enum SmartPayOnboardingSteps {
    OVERVIEW = 'OVERVIEW',
    USER_INFORMATION = 'USER_INFORMATION',
    COMPANY_INFORMATION = 'COMPANY_INFORMATION',
    PLAN = 'PLAN',
    SUCCESS = 'SUCCESS',
}

export interface NFCustomerDataType {
    customerType: SmartPayCustomerType;
    cvrAlreadyOnboarded: boolean;
    anotherOnboardingAlreadyInProgress: boolean;
    allCustomersOnboarded: boolean;
    novemberFirstCustomerNumberList: string[];
}

export interface SmartPayOnboardingFormData {
    email: string;
    newsletter: boolean;
    firstName: string;
    lastName: string;
    phone: string;
    cvr: string;
    company: string;
    nfCustomerNumber: string | null;
    bankAccount: number;
    journalNumber: number | null;
    feeJournalNumber: number;
    subscriptionType: number;
}

export enum SmartPayCustomerType {
    NewCustomer = 1,
    ExistingCustomerWithIntegration = 2,
    ExistingCustomerWithoutIntegration = 3,
}

export enum SmartPayIntegrationError {
    // error codes also act as translation keys
    SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_DEFAULT_MESSAGE = 'SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_DEFAULT_MESSAGE',
    SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_ACCOUNT_NUMBER_LENGTH_INVALID = 'SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_ACCOUNT_NUMBER_LENGTH_INVALID',
    SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_BANK_NOT_SUPPORTED = 'SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_BANK_NOT_SUPPORTED',
    NEWSLETTER_SUBSCRIBE = 'NEWSLETTER_SUBSCRIBE',
    SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_NOVEMBERFIRST_INTEGRATION_EXISTS = 'SMARTPAY_ONBOARDING_MODAL_ERROR_STATE_NOVEMBERFIRST_INTEGRATION_EXISTS',
}

export interface SmartPayIntegrationModel {
    refetchIntegrationState: () => Promise<void>;
    daybook?: number;
    paymentAccount?: number;
    feeAccount?: number;
    novemberFirstCustomerNumber: string;
    cvr: string;
    contact: string;
    telephone: string;
    email: string;
    customerType?: SmartPayCustomerType;
    status: SmartPayOnboardingStatus;
    onboardingNfUrl: string;
    lastUpdatedDate?: Date;
    autoWithdrawActiveDate?: Date;
    subscriptionType: 0 | 1 | 2;
    unsubscriptionDate?: Date;
    isPaymentCreationEnabled: boolean;
    isMiscPaymentActionEnabled: boolean;
}

export const DEFAULT_FEE_ACCOUNT_NUMBER = 3628;

export interface SmartPayIntegrationMissingInformationModel {
    informationType: 'Id' | 'ProofOfAddress' | 'Consent' | 'AdditionalInformation' | 'Other';
    name: string;
    email: string;
    requestDate: Date;
    solvedDate?: Date;
}

export const SMARTPAY_PLAN_OPTIONS = [
    {
        id: 1,
        name: <Trans>Pay-per-use</Trans>,
        description: <Trans>If you have few domestic payments</Trans>,
        price: <Trans>0 kr./month</Trans>,
        features: (
            <Trans>
                <div>
                    <span className="font-bold text-lg text-blue-500">+</span> 4 kr. fee per payment (DKK and EUR in SEPA zone)
                </div>
                <div>
                    <span className="font-bold text-lg text-blue-500">+</span> 40 kr. fee per other foreign payment
                </div>
            </Trans>
        ),
    },
    {
        id: 2,
        name: <Trans>Subscription</Trans>,
        description: <Trans>If you have foreign payments or many domestic payments</Trans>,
        price: <Trans>199 kr./month</Trans>,
        features: (
            <Trans>
                <div>
                    <span className="font-bold text-lg text-blue-500">+</span> 1 kr. fee per payment (DKK and EUR in SEPA zone)
                </div>
                <div>
                    <span className="font-bold text-lg text-blue-500">+</span> 30 kr. fee per other foreign payment
                </div>
                <div>
                    <span className="font-bold text-lg text-blue-500">+</span> 20% saving on the currency margin
                </div>
            </Trans>
        ),
    },
];

export enum SmartPayOnboardingStatus {
    // User has no N1 integration added
    Unregistered = 1,
    // User has added the integration and stored their settings (journal, bank account), awaiting N1 email
    Pending = 2,
    // User has used their one-time password and begun onboarding
    CustomerVerifyingData = 3,
    // New customer type clicked continue onboarding while he was in CustomerVerifyingData state
    ContinuedOnboarding = 4,
    AwaitingCustomerInformation = 10,
    // User has completed N1 onboarding and is awaiting validation and approval by N1 and NETS
    CustomerReadyForValidation = 5,
    CustomerReadyForApproval = 6,
    // User has been validated and approved by N1 and NETS and can now make payments
    Completed = 7,
    // failure states
    Rejected = 8,
    Blocked = 9,
}

// PAYMENTS

export interface SuppliersResponse {
    pagination: PaginationResponse;
    data: Supplier[];
}
export interface PaymentsResponse {
    pagination: PaginationResponse;
    data: SmartPayPaymentExtendedModel[];
}

export interface PaginationResponse {
    count: number;
    pageSize: number;
    returnAll: boolean;
    skipPages: number;
}

export interface SmartPayPaymentsFilters {
    fromDate: Date;
    toDate: Date;
    supplier: string;
    supplierGroup: string;
    status: string;
    includeDaybooks: boolean;
    senderBankAccount: string;
    journalIds?: string;
}

export interface SmartPayPaymentsDefaultFilters {
    filter: SmartPayPaymentsFilters;
    yearSpecialPeriodName: string;
}

export interface SmartPayPaymentsSettings {
    combineInvoices?: boolean;
    paymentShouldArriveOnDueDate?: boolean;
}
export interface SmartPayIntegrationSettings {
    PaymentAccount?: number | null;
    Daybook?: number | null;
    FeeAccount?: number | null;
}
export interface SmartPayHandledPayment extends SmartPayPaymentExtendedModel, SmartPayPaymentExtendedDetailsModel {}

// internnal-api type generator doesn't pick this one, creating it manually for now
export interface SmartPayBankAccountDto {
    bankName: string;
    bankAccount: string;
}
export enum SmartPayPaymentExcludeType {
    Remove = 0,
    MarkedAsPaid = 1,
}
export enum SmartPayPaymentAccountStatus {
    NotOnNovemberFirst = 'NotOnNovemberFirst',
    ActiveOS = 'ActiveOS',
    AppliedOS = 'AppliedOS',
    InactiveOS = 'InactiveOS',
}
export enum PaymentStatuses {
    New = 'NEW',
    Creating = 'CREATING',
    Pending = 'PENDING',
    PendingApproval = 'PENDING_APPROVAL',
    PartiallyApproved = 'PARTIALLY_APPROVED',
    Rejected = 'REJECTED',
    Scheduled = 'SCHEDULED',
    Processing = 'PROCESSING',
    TradeReverted = 'TRADE_REVERTED',
    TransferForbidden = 'TRANSFER_FORBIDDEN',
    Cancelled = 'CANCELLED',
    Invalid = 'INVALID',
    Failed = 'FAILED',
    Completed = 'COMPLETED',
    CreditNote = 'CREDIT_NOTE',
    MissingEntries = 'MISSING_ENTRIES',
    MarkedAsPaid = 'MARKED_AS_PAID',
}

export enum InboxPaymentStatuses {
    Invalid = -1,
    ApprovalRequired = 0,
    Failed = 1,
    Pending = 2,
    PartiallyApproved = 3,
    Rejected = 4,
    Cancelled = 5,
    // TakenBack = 6,
    TradeReverted = 7,
    TransferForbidden = 8,
    Scheduled = 9,
    Processing = 10,
    Completed = 11,
    MarkedAsPaid = 12,
    Creating = 13,
    Deleted = 14,
}

export const UNPAID_PAYMENTS_STATUSES = [
    PaymentStatuses.New,
    PaymentStatuses.Creating,
    PaymentStatuses.Rejected,
    PaymentStatuses.TradeReverted,
    PaymentStatuses.TransferForbidden,
    PaymentStatuses.Cancelled,
    PaymentStatuses.Failed,
    PaymentStatuses.Invalid,
    PaymentStatuses.CreditNote,
];

export const PENDING_APPROVAL_PAYMENTS_STATUSES = [
    PaymentStatuses.Pending,
    PaymentStatuses.PendingApproval,
    PaymentStatuses.PartiallyApproved,
];

export const SCHEDULED_PAYMENTS_STATUSES = [PaymentStatuses.Scheduled, PaymentStatuses.Processing];
export const PAID_PAYMENTS_STATUSES = [PaymentStatuses.Completed, PaymentStatuses.MissingEntries, PaymentStatuses.MarkedAsPaid];

export const TABLE_SETTINGS_LOCALSTORAGE_KEY = 'smartpay-payments-table-settings';
export const PENDING_TABLE_SETTINGS_LOCALSTORAGE_KEY = 'smartpay-pending-payments-table-settings';
export const SCHEDULED_TABLE_SETTINGS_LOCALSTORAGE_KEY = 'smartpay-scheduled-payments-table-settings';
export const PAID_TABLE_SETTINGS_LOCALSTORAGE_KEY = 'smartpay-paid-payments-table-settings';
export const ORPHANED_TABLE_SETTINGS_LOCALSTORAGE_KEY = 'smartpay-orphaned-payments-table-settings';
export const MATCH_PAYMENT_CREDIT_NOTE_LOCALSTORAGE_KEY = 'smartpay-match-payment-credit-note-table-settings-adjusted';
export enum SmartpaySettingsModalTabs {
    Setup = 'Setup',
    BookKeeping = 'BookKeeping',
    BankAccounts = 'BankAccounts',
    UsersAndApprovals = 'UsersAndApprovals',
    MySetup = 'MySetup',
}

export enum SmartPayUserRoles {
    PaymentCreator = 'PAYMENT_CREATOR',
    PaymentApprover = 'PAYMENT_APPROVER',
}

export interface RejectPayload {
    paymentReference: string;
    rejectReason: string | undefined;
}

export interface SmartPayRemovePayload {
    paymentSourceId: number;
    paymentSourceTypeId: SmartPayPaymentSourceType;
    excludeType: SmartPayPaymentExcludeType;
}

export type SmartPayPaymentExtendedModelWithRejectionReason = SmartPayPaymentExtendedModel & {
    reasonForRejection?: string;
};

export const TABLE_PAGE_SIZE = 50;

export const CREATE_PAYMENTS_BATCH_SIZE = 10;

export const EXCLUDE_PAYMENTS_BATCH_SIZE = 5;

export const NonDKKorEURpaymentFee = 140;

export interface CountAndTotal {
    count: number;
    total: number;
}

export const RETURN_FROM_NF_APPROVAL_FLOW_URL_PARAM = 'OfCourseIStillLoveYou';
export const SMARTPAY_WOOTRIC_SURVEY_LOCAL_STORAGE_KEY = 'smartpay-native-has-wootric-survery-been-shown';
export interface ExcludePayments {
    rows: SmartPayPaymentExtendedModel[] | SmartPayCandidatePayment[];
    type: SmartPayPaymentExcludeType;
}

export interface SmartPayCreatePaymentItem {
    amount: number;
    paymentSourceId: number;
    coverRecipientFee?: boolean;
}

export interface SmartPayCreatePaymentPayload {
    payments: SmartPayCreatePaymentItem[];
    paymentAccountId: number;
    notifyApprovers: boolean;
}

export const smartPayValidAccountTypes: AccountTypeNumber[] = [AccountTypeNumber.ProfitAndLoss, AccountTypeNumber.BalanceSheet];

export interface BannerNotification {
    enabled: boolean;
    message?: string | JSX.Element;
    state?: State;
    showLinkToStatusPage?: boolean;
}

export interface NfEcoMatchedUser {
    economicUserGuid?: string;
    economicEmail?: string;
    firstName: string;
    lastName: string;
    novemberFirstEmail: string;
    isEnabled: boolean;
    isPaymentApprover: boolean;
    isAutomaticMatch: boolean;
    transferLimit: number;
    role: NovemberFirstRole;
}

export enum NovemberFirstRole {
    AccountOwner = 'AccountOwner',
    User = 'User',
}

export interface NfEcoMatchedUserWithRole extends NfEcoMatchedUser {
    economicRole?: 'Administrator' | 'User' | 'SuperUser';
}
export interface SmartPayPaymentApproverSettingsApiResponse {
    approvalType: NovemberFirstApprovalType;
    users: NfEcoMatchedUser[];
    isCurrentUserPaymentApprover: boolean;
}

export enum NovemberFirstApprovalType {
    Disable = 0,
    SingleApprover = 1,
    MultipleApprovers = 2,
}

export enum SmartPayPaymentSourceType {
    JOURNAL_ENTRY = 1,
    DRAFT_ENTRY = 3, // only for marked as paid entries
    BOOKED_ENTRY = 4, // only for marked as paid entries
    INBOX_DOCUMENT = 2,
}

export interface SmartPayCandidatePayment {
    paymentSourceId: number;
    paymentSourceTypeId: SmartPayPaymentSourceType;

    invoiceNumber: string;
    invoiceCurrency: string;
    invoiceAmount: number;
    paymentTypeId: number;
    attachmentNumber?: number | undefined;
    dueDate: CSharpDateTime;

    supplierId: number;
    supplierName: string;
    supplierCountry: string;
    paymentSupplierIdentifier: string;
    paymentSupplierInvoiceIdentifier: string;

    invoices: SmartPayPaymentInvoiceModel[];
    smartPayPaymentId?: number;
    status: PaymentStatuses;
    paymentDate: CSharpDateTime;
    suggestedPaymentDate?: CSharpDateTime;
    fee: number;
    amountToBePaid: number;
    invoiceTotalInBaseCurrency: number;
    amountInBaseCurrency: number;
    draftRemainingAmount: number;
    exchangeRate: number;
    validationMessages?: string[];
}

export interface SmartPayCandidatePaymentDuplicates extends SmartPayCandidatePayment {
    possibleDuplicatePayments: SmartPayPaymentDuplicateModel[];
}

interface SmartPayPaymentDuplicateModel {
    paymentReference: string;
}
